import React from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SearchableDropdown } from '@/components/shared/SearchableDropdown';
import trashBinTrashIcon from '@/assets/images/trash-bin-trash.svg';
import type { ExpandedFoodRow, FoodSearchResult } from './nutritionalAnalysisTypes';
import { buildRowUnitSelectOptions, isIngredientRow } from './nutritionalAnalysisPure';
import { nutritionItemAddingColumnSx } from '@/components/meal-planning/manual/NutritionItemAddingState';

export type NutritionalAnalysisNutritionRowProps = {
  mealName: string;
  rowIndex: number;
  row: ExpandedFoodRow;
  nutritionTableColumns: string;
  quantityLocked: boolean;
  isQuantitySyncing?: boolean;
  isAddingItem?: boolean;
  isDeletingItem?: boolean;
  hasActiveRowDeletion?: boolean;
  isSavingMealSlot: boolean;
  searchResults: FoodSearchResult[];
  isSearching: boolean;
  showResults: boolean;
  onItemTextChange: (value: string) => void;
  onSelectSearchResult: (item: FoodSearchResult) => void;
  onCloseSearch: () => void;
  onFocusSearch: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onAdjustQty: (delta: number) => void;
  qtyDraft?: string;
  onQtyDraftChange?: (rawValue: string) => void;
  onUpdateField: (field: keyof ExpandedFoodRow, value: string | number) => void;
  onDownloadRow?: (event: React.MouseEvent) => void;
  isDownloadingRow?: boolean;
  onEditRow: (event: React.MouseEvent) => void;
  onDeleteRow: () => void;
  isLastRow: boolean;
  /** When true, show a leading checkbox for recipe/ingredient rows with an item selected. */
  showRowCheckbox?: boolean;
  rowCheckboxChecked?: boolean;
  onRowCheckboxChange?: (checked: boolean) => void;
  /** Public template viewer layout (no ACTIONS column, simplified row). */
  readOnly?: boolean;
  /** Non-editable template (e.g. private locked): keep full grid, disable inputs. */
  interactionDisabled?: boolean;
};

const NutritionalAnalysisNutritionRow: React.FC<NutritionalAnalysisNutritionRowProps> = ({
  mealName,
  rowIndex,
  row,
  nutritionTableColumns,
  quantityLocked,
  isQuantitySyncing = false,
  isAddingItem = false,
  isDeletingItem = false,
  hasActiveRowDeletion = false,
  isSavingMealSlot,
  searchResults,
  isSearching,
  showResults,
  onItemTextChange,
  onSelectSearchResult,
  onCloseSearch,
  onFocusSearch,
  onAdjustQty,
  qtyDraft,
  onQtyDraftChange,
  onUpdateField,
  onDownloadRow,
  isDownloadingRow = false,
  onEditRow,
  onDeleteRow,
  isLastRow,
  showRowCheckbox = false,
  rowCheckboxChecked = false,
  onRowCheckboxChange,
  readOnly = false,
  interactionDisabled = false,
}) => {
  const ingredientRow = isIngredientRow(row);
  const inputsLocked = interactionDisabled && !readOnly;
  const quantityInputsLocked = quantityLocked || inputsLocked || isQuantitySyncing || isAddingItem;
  const quantityDisplayValue = qtyDraft ?? String(row.qty);
  const canShowEditIcon = !ingredientRow;
  const canShowDownloadIcon = Boolean(
    onDownloadRow
    && row.itemType === 'recipe'
    && String(row.item ?? '').trim().length > 0,
  );
  const hasVisibleItem = String(row.item ?? '').trim().length > 0;
  const hasPickableItem =
    (row.itemType === 'recipe' || row.itemType === 'ingredient') && hasVisibleItem;
  const showLeadingSelectColumn = Boolean(showRowCheckbox);
  const unitSelectOptions = hasVisibleItem
    ? buildRowUnitSelectOptions(row)
    : [{ value: '', label: '-' }];
  const selectedUnitValue = hasVisibleItem
    ? (isIngredientRow(row) ? row.unit : row.serving)
    : '';
  const selectedUnitLabel =
    unitSelectOptions.find((option) => option.value === selectedUnitValue)?.label ?? String(selectedUnitValue ?? '');

  return (
  <Box
    sx={{
      px: 2,
      py: readOnly ? 1 : 1.25,
      bgcolor: readOnly ? '#ffffff' : '#F1F8FF',
      border: readOnly ? '1px solid #e8edf3' : 'none',
      borderRadius: '8px',
      display: 'grid',
      gridTemplateColumns: nutritionTableColumns,
      alignItems: 'center',
      gap: readOnly ? 1.1 : 1,
      mb: isLastRow ? 0 : readOnly ? 0.65 : 0.8,
      boxShadow: readOnly ? '0 1px 0 rgba(15, 23, 42, 0.04)' : 'none',
      '& > *': {
        minWidth: 0,
      },
    }}
  >
    {showLeadingSelectColumn ? (
      hasPickableItem ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', justifySelf: 'center' }}>
          <Checkbox
            size="small"
            checked={Boolean(rowCheckboxChecked)}
            onChange={(e) => onRowCheckboxChange?.(e.target.checked)}
            disabled={interactionDisabled}
            inputProps={{
              'aria-label': `Select ${String(row.item ?? 'food row').trim() || 'option'}`,
            }}
            sx={{ p: 0.35 }}
          />
        </Box>
      ) : (
        <Box sx={{ width: 44, minHeight: 36 }} aria-hidden />
      )
    ) : null}
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        px: 1,
        py: 1,
        borderRadius: '8px',
        bgcolor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        minHeight: 36,
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease, outline 0.2s ease',
        ...(isAddingItem ? nutritionItemAddingColumnSx : {}),
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      }}
      {...(!readOnly && !interactionDisabled ? { 'data-row-search-anchor': 'true' } : {})}
      data-meal-name={mealName}
      data-row-type={ingredientRow ? 'ingredient' : 'food'}
      data-row-empty={String(!String(row.item ?? '').trim())}
    >
      {(row.itemType === 'recipe' || row.itemType === 'ingredient') && String(row.item ?? '').trim().length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.45, minWidth: 0, position: 'relative', zIndex: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, minWidth: 0 }}>
            <Tooltip
              title={String(row.item ?? '').trim()}
              followCursor
              enterDelay={0}
              enterNextDelay={0}
              leaveDelay={0}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: 0,
                  width: '100%',
                  color: isAddingItem ? '#667085' : '#364153',
                  px: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.item}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      ) : readOnly ? (
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#98a2b3', px: 0.25 }}>—</Typography>
      ) : (
        <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', gap: 0.85, minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
          <SearchableDropdown<FoodSearchResult>
            value={row.item}
            onChange={onItemTextChange}
            onSelect={onSelectSearchResult}
            searchResults={searchResults}
            isSearching={isSearching}
            isAdding={isAddingItem}
            showResults={showResults && !isAddingItem}
            onClose={onCloseSearch}
            onFocus={onFocusSearch}
            disabled={inputsLocked || isAddingItem}
          minSearchLength={2}
          placeholder="Search food or ingredient..."
          searchResultsHeader="Search Results"
          emptyStateText="No matching recipes or ingredients"
          containerClassName="w-full"
          className="w-full [&>div.absolute]:hidden"
          inputClassName="h-7 text-[14px] leading-[20px] bg-white w-full [&_.MuiOutlinedInput-input]:py-[6px] [&_.MuiOutlinedInput-input]:pl-0 [&_.MuiOutlinedInput-input]:pr-7 [&_.MuiOutlinedInput-input]:text-left [&_.MuiOutlinedInput-input]:font-normal [&_.MuiOutlinedInput-input]:tracking-[0] border-0 focus:border-transparent focus:ring-0"
          dropdownClassName="max-h-56 min-w-[360px]"
          portalResults
          colorPrefix="blue"
          getItemId={(item) => `${item.type}-${item.id}-${item.sourceId ?? ''}`}
          getItemDisplayName={(item) => item.name}
        />
          </Box>
        </Box>
      )}
    </Box>
    {readOnly ? (
      <Typography
        sx={{
          justifySelf: 'center',
          width: 106,
          textAlign: 'center',
          fontSize: '14px',
          color: '#364153',
          lineHeight: '20px',
        }}
      >
        {row.qty}
      </Typography>
    ) : (
      <Box
        sx={{
          width: 106,
          height: 35,
          borderRadius: '999px',
          bgcolor: '#ffffff',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          alignItems: 'center',
          overflow: 'hidden',
          justifySelf: 'center',
          columnGap: '4px',
        }}
      >
        <IconButton
          size="small"
          onClick={() => onAdjustQty(-0.5)}
          disabled={quantityInputsLocked || Number(row.qty) <= 0.1}
          sx={{
            borderRadius: 0,
            p: 0,
            color: '#5b6470',
            bgcolor: '#ededed',
            width: 24,
            height: '100%',
            justifySelf: 'start',
            '&:hover': { bgcolor: '#e5e7eb' },
          }}
        >
          <Typography sx={{ textAlign: 'center', fontSize: '1rem', color: '#5b6470', lineHeight: 1 }}>-</Typography>
        </IconButton>
        <Box sx={{ height: '100%', bgcolor: '#ffffff', display: 'grid', placeItems: 'center' }}>
          {isQuantitySyncing ? (
            <CircularProgress size={14} sx={{ color: '#98a2b3' }} />
          ) : (
            <InputBase
              type="text"
              inputMode="decimal"
              value={quantityDisplayValue}
              onChange={(event) => onQtyDraftChange?.(event.target.value)}
              onFocus={(event) => event.currentTarget.select()}
              disabled={quantityInputsLocked}
              sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  p: 0,
                  fontSize: '14px',
                  color: '#364153',
                  lineHeight: '20px',
                  width: 42,
                },
              }}
            />
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => onAdjustQty(0.5)}
          disabled={quantityInputsLocked}
          sx={{
            borderRadius: 0,
            p: 0,
            color: '#5b6470',
            bgcolor: '#ededed',
            width: 24,
            height: '100%',
            justifySelf: 'end',
            '&:hover': { bgcolor: '#e5e7eb' },
          }}
        >
          <Typography sx={{ textAlign: 'center', fontSize: '1rem', color: '#5b6470', lineHeight: 1 }}>+</Typography>
        </IconButton>
      </Box>
    )}
    {readOnly ? (
      <Typography
        component="div"
        sx={{
          minWidth: 0,
          maxWidth: 260,
          width: '100%',
          justifySelf: 'center',
          textAlign: 'center',
          fontSize: '14px',
          color: '#364153',
          lineHeight: '20px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={selectedUnitLabel}
      >
        {selectedUnitLabel}
      </Typography>
    ) : (
      <Box
        sx={{
          px: 0,
          py: 0,
          minHeight: 35,
          height: 35,
          boxSizing: 'border-box',
          borderRadius: '4px',
          border: '1px solid #eaeaea',
          bgcolor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'stretch',
          width: 104,
          minWidth: 104,
          maxWidth: 104,
          flexShrink: 0,
          overflow: 'visible',
          justifySelf: 'center',
        }}
      >
        <Select
          value={selectedUnitValue}
          onChange={(event) =>
            onUpdateField(
              isIngredientRow(row) ? 'unit' : 'serving',
              event.target.value,
            )
          }
          disabled={inputsLocked || isQuantitySyncing}
          SelectDisplayProps={{ title: selectedUnitLabel }}
          variant="standard"
          disableUnderline
          IconComponent={KeyboardArrowDownRoundedIcon}
          MenuProps={{
            disablePortal: false,
            keepMounted: true,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: {
              sx: {
                mt: 0.5,
                minWidth: 170,
                maxHeight: 280,
                borderRadius: '10px',
                border: '1px solid #d9e1e9',
                boxShadow: '0 12px 24px rgba(16,24,40,0.12)',
                zIndex: 14000,
              },
            },
          }}
          sx={{
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            height: '100%',
            bgcolor: 'transparent',
            position: 'relative',
            overflow: 'visible',
            '&.MuiInputBase-root': {
              overflow: 'visible',
            },
            '& .MuiSelect-select': {
              textAlign: 'left',
              py: 0,
              pl: '4px',
              pr: '20px !important',
              fontSize: '14px',
              color: '#364153',
              lineHeight: '33px',
              height: '100%',
              minHeight: 0,
              minWidth: 0,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .MuiSelect-icon': {
              fontSize: 18,
              color: '#1f2937',
              right: 0,
              top: 'calc(50% - 9px)',
              width: 18,
              height: 18,
              margin: 0,
              zIndex: 2,
              opacity: 1,
              pointerEvents: 'none',
            },
          }}
        >
          {unitSelectOptions.map(({ value, label }) => (
            <MenuItem key={`${mealName}-${rowIndex}-serving-${value}`} value={value} title={label} sx={{ fontSize: '0.75rem' }}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    )}
    <Typography
      sx={{
        justifySelf: 'center',
        width: 68,
        textAlign: 'center',
        p: 0,
        fontSize: '14px',
        color: '#f59e0b',
        fontWeight: 600,
        lineHeight: '20px',
      }}
    >
      {row.kcal}
    </Typography>
    <Typography
      sx={{
        justifySelf: 'center',
        width: 56,
        textAlign: 'center',
        p: 0,
        fontSize: '14px',
        color: '#111827',
        fontWeight: 600,
        lineHeight: '20px',
      }}
    >
      {row.protein}
    </Typography>
    <Typography
      sx={{
        justifySelf: 'center',
        width: 56,
        textAlign: 'center',
        p: 0,
        fontSize: '14px',
        color: '#111827',
        fontWeight: 600,
        lineHeight: '20px',
      }}
    >
      {row.carbs}
    </Typography>
    <Typography
      sx={{
        justifySelf: 'center',
        width: 44,
        textAlign: 'center',
        p: 0,
        fontSize: '14px',
        color: '#111827',
        fontWeight: 600,
        lineHeight: '20px',
      }}
    >
      {row.fat}
    </Typography>
    {!readOnly ? (
      <Stack direction="row" spacing={0.4} alignItems="center" justifySelf="center">
        {canShowDownloadIcon ? (
          <Tooltip title={isDownloadingRow ? 'Downloading recipe' : 'Download recipe'}>
            <span>
              <IconButton
                type="button"
                size="small"
                disabled={isSavingMealSlot || interactionDisabled || isDownloadingRow || isAddingItem}
                onClick={onDownloadRow}
                aria-label="Download recipe"
                sx={{
                  p: 0.25,
                  ml: 0.2,
                  color: '#98a2b3',
                  '&.Mui-disabled': { color: '#cbd5e1' },
                }}
              >
                {isDownloadingRow ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DownloadRoundedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
        {!ingredientRow && canShowEditIcon ? (
          <IconButton
            type="button"
            size="small"
            disabled={isSavingMealSlot || interactionDisabled || isAddingItem}
            onClick={onEditRow}
            aria-label="Edit row"
            sx={{
              p: 0.25,
              ml: 0.2,
              color: '#98a2b3',
              '&.Mui-disabled': { color: '#cbd5e1' },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
        <IconButton
          type="button"
          size="small"
          onClick={onDeleteRow}
          disabled={interactionDisabled || isAddingItem || isDeletingItem || (hasActiveRowDeletion && !isDeletingItem)}
          aria-label={isDeletingItem ? 'Removing row' : 'Delete row'}
          sx={{ p: 0.25, ml: 0.2 }}
        >
          {isDeletingItem ? (
            <CircularProgress size={14} sx={{ color: '#98a2b3' }} />
          ) : (
            <Box
              component="img"
              src={trashBinTrashIcon}
              alt=""
              sx={{ width: 16, height: 16, opacity: 0.9 }}
            />
          )}
        </IconButton>
      </Stack>
    ) : null}
  </Box>
  );
};

export default NutritionalAnalysisNutritionRow;
